<template>
  <div
    class="sf-bg-white sf-full-height tw-overflow-auto"
    :class="{
      'tw-w-full': isUsingNewLayout(),
      'tw-w-[406px]': !isUsingNewLayout() && !inFloatingSidepane,
    }"
    data-testid="sf-insights-left-panel"
  >
    <div
      class="tw-py-3 tw-px-3 tw-flex tw-gap-4 tw-w-full"
      :class="{
        'sf-layer-border-bottom tw-mt-[-1px]': !inFloatingSidepane,
        'tw-border-0 tw-border-b tw-border-solid tw-border-sf-slate-lighter-1':
          inFloatingSidepane,
      }"
    >
      <SfButtonIcon
        v-if="!inFloatingSidepane"
        icon="arrow-left"
        @click="closeLeftDrawer"
      />
      <div
        class="
          tw-w-full
          sf-btn sf-level sf-level-around
          rs-select-segment
          sf-c-primary
        "
        @click="showSegmentModal"
      >
        <div class="sf-level sf-level-around">
          <div
            class="sf-level"
            data-walkthrough-id="all-preset-queries"
            data-testid="see-all-preset-queries-btn"
          >
            {{ I18n.t('companies_overview.see_all_preset_queries') }}
          </div>
        </div>
      </div>
    </div>

    <SegmentInfo v-if="displayedFilters.length && !getError" />
    <SegmentSelector />

    <div
      v-if="!queryOverviewEnabled"
      class="tw-py-4 tw-px-4 tw-border-0 tw-border-b tw-border-solid"
      :class="
        inFloatingSidepane
          ? 'tw-border-sf-slate-lighter-1'
          : 'tw-border-sf-neutral-lighter-1'
      "
    >
      <div
        class="sf-below-1"
        :class="
          inFloatingSidepane
            ? 'tw-text-sm tw-font-medium'
            : 'sf-title sf-font-l'
        "
        data-testid="period-selection-title"
      >
        {{ I18n.t('companies_overview.label_period_selection') }}
        <div
          v-if="isSegmentLoading"
          class="sf-icon sf-spinner tw-inline-block tw-ml-1"
        />
      </div>

      <div v-if="!isSegmentLoading">
        <template v-if="periodSelection">
          <PeriodSelectionInfo data-testid="segmentSelectedPeriod" />
        </template>

        <template v-else>
          <div class="tw-text-neutral-700 tw-my-3">
            {{ I18n.t('companies_overview.no_period_selected') }}
          </div>
        </template>

        <div
          class="sf-btn rs-open-period-selection sf-level sf-level-around"
          :class="selectPeriodClass"
          data-walkthrough-id="select-period"
          @click="openPeriodSelection"
        >
          {{ selectPeriodLabel }}
        </div>
      </div>
    </div>

    <div
      v-if="!queryOverviewEnabled"
      class="tw-p-4 rs-filter-list tw-border-0 tw-border-b tw-border-solid"
      :class="
        inFloatingSidepane
          ? 'tw-border-sf-slate-lighter-1'
          : 'tw-border-sf-neutral-lighter-1'
      "
    >
      <FiltersSectionHeader
        :active-filters-count="activeFilters.length"
        :segment-loading="isSegmentLoading"
        @add="openFiltersModal"
      />

      <div v-if="!isSegmentLoading">
        <ActiveFiltersList
          v-if="activeFilters.length"
          :edit-filter="editFilter"
        ></ActiveFiltersList>

        <div
          v-else
          class="
            tw-text-center
            tw-p-6
            tw-bg-sf-neutral-subtle-2
            tw-text-sf-gray-light
            tw-text-sm
          "
        >
          <div class="tw-mb-1 tw-font-medium">
            {{ I18n.t('companies_overview.filters.no_filters_applied') }}
          </div>

          <div>
            {{ I18n.t('companies_overview.filters.no_filters_applied_info') }}
          </div>
        </div>

        <SfButton
          data-testid="sf-insights-left-panel-add-filter-button"
          class="tw-w-full tw-mt-3"
          variant="info"
          @click="openFiltersModal"
        >
          <span class="sf-icon fa fa-plus" />
          <span>
            {{ I18n.t('companies_overview.filters.add_filter_button') }}
          </span>
        </SfButton>
      </div>
    </div>

    <template v-if="segment && manageTrigger">
      <div
        class="tw-p-4 tw-border-0 tw-border-b tw-border-solid"
        :class="
          inFloatingSidepane
            ? 'tw-border-sf-slate-lighter-1'
            : 'tw-border-neutral-200'
        "
      >
        <div
          class="sf-below-1"
          :class="
            inFloatingSidepane
              ? 'tw-text-sm tw-font-medium'
              : 'sf-title sf-font-l'
          "
        >
          {{ I18n.t('companies_overview.triggers_new') }}
          <div
            v-if="triggerLoading"
            class="sf-spinner-wrapper sf-pad-l-1 sf-inline-block"
          >
            <div class="sf-spinner"></div>
          </div>
        </div>
        <template v-if="showTriggers">
          <div class="sf-pad-t-2 sf-below-3 rs-triggers">
            <div
              v-for="trigger in queryTriggers"
              :key="trigger.id"
              class="sf-level sf-below-2 sf-pointer"
              @click="setEditedTrigger(trigger)"
            >
              <div>
                <div v-if="trigger.running" class="sf-spinner"></div>
                <div
                  v-if="!trigger.running"
                  class="fa fa-paper-plane sf-c-primary"
                ></div>
              </div>
              <div
                :class="{ 'sf-disabled': triggerLoading }"
                class="
                  sf-c-text-dark sf-fwdb sf-font-m sf-pointer sf-dim-el-hover
                  rs-trigger-title
                "
              >
                {{ trigger.title }}
              </div>
            </div>
          </div>
        </template>
        <template v-else>
          <div
            v-if="!triggerLoading"
            class="sf-text sf-c-text-light sf-pad-r-4 sf-pad-b-2"
          >
            {{ I18n.t('companies_overview.no_triggers_yet_new') }}
          </div>
        </template>
        <div
          v-if="addTriggerAllowed"
          v-tooltip="addTriggerTooltip"
          class="sf-block"
        >
          <a
            :class="{ 'sf-disabled': addTriggerDisabled }"
            class="sf-btn sf-level sf-level-around sf-c-primary rs-add-trigger"
            @click="addTrigger"
            >{{ I18n.t('companies_overview.add_trigger') }}</a
          >
        </div>
      </div>
    </template>

    <AddFilterModal />
  </div>
</template>

<script>
import { mapActions as mapPiniaActions, mapState as mapPiniaState } from 'pinia'
import { useDrawer } from '@app/shared/pinia/drawer'
import { isUsingNewLayout } from '@app/adapters/silverfin'
import { useCompaniesStore } from '../store/companies'
import { useSegmentsStore } from '../store/segments'
import { useSubmissionsStore } from '../store/submissions'
import { useUsersStore } from '../store/users'
import { useReconciliationTextsStore } from '../store/reconciliation-texts'
import { useTriggersStore } from '../store/triggers'
import { useFiltersStore } from '../store/filters'
import { useInsightsRootStore } from '../store/insights-root'
import { useNavigationStore } from '../store/navigation'
import {
  SfButtonIcon,
  SfButton,
} from '@app/shared/vue/components/sf-button/index'

import ActiveFiltersList from './filters/active-filters-list'
import SegmentInfo from './segments/segment-info'
import SegmentSelector from './segments/segment-selector'
import PeriodSelectionInfo from './period-selection-info.vue'
import AddFilterModal from './filters/add-filter-modal.vue'
import FiltersSectionHeader from './filters/filters-section-header.vue'
import { INSIGHTS_LAYOUT_CONTEXT_KEY } from '../composables/use-insights-layout-context'

export default {
  name: 'InsightsLeftPanel',
  components: {
    ActiveFiltersList,
    SegmentInfo,
    SegmentSelector,
    SfButtonIcon,
    SfButton,
    PeriodSelectionInfo,
    AddFilterModal,
    FiltersSectionHeader,
  },

  inject: {
    insightsLayoutContext: {
      from: INSIGHTS_LAYOUT_CONTEXT_KEY,
      default: () => ({ inFloatingSidepane: false }),
    },
  },

  computed: {
    inFloatingSidepane() {
      return this.insightsLayoutContext.inFloatingSidepane
    },
    ...mapPiniaState(useCompaniesStore, [
      'getError',
      'pendingCompaniesRequest',
      'allCompaniesProcessed',
    ]),
    ...mapPiniaState(useSegmentsStore, [
      'segment',
      'isSegmentLoading',
      'isSegmentModified',
    ]),

    ...mapPiniaState(useTriggersStore, [
      'editedTrigger',
      'triggerLoading',
      'manageTrigger',
      'queryTriggers',
    ]),

    ...mapPiniaState(useFiltersStore, ['activeFilters', 'displayedFilters']),

    ...mapPiniaState(useInsightsRootStore, [
      'periodSelection',
      'ledgerSelectDate',
      'ledgerSelectLockType',
      'periodSelectionOpen',
      'queryOverviewEnabled',
    ]),
    ...mapPiniaState(useDrawer, ['isLeftDrawerOpen']),

    showTriggers() {
      return this.queryTriggers.length
    },

    addTriggerAllowed() {
      // segment loading -> render the button. However, it will be disabled. Prevent "jumps" when switching segments.
      return true
    },

    isPartnerSegment() {
      return this.$route.query.partner_segment_id || this.segment?.recommended
    },

    addTriggerDisabled() {
      return (
        this.isSegmentLoading ||
        this.isSegmentModified ||
        this.isPartnerSegment ||
        this.segment?.incompatibility_reasons?.length
      )
    },

    addTriggerTooltip() {
      if (this.isSegmentModified) {
        return I18n.t(
          'companies_overview.save_query_to_enable_trigger_creation'
        )
      }
      if (this.isPartnerSegment) {
        return I18n.t(
          'companies_overview.copy_recommended_query_to_enable_trigger_creation'
        )
      }
      if (this.segment?.incompatibility_reasons?.length) {
        return this.segment.incompatibility_reasons
          .map((reason) =>
            I18n.t(
              `insight_triggers.incompatibility.${reason}_creation_disabled`
            )
          )
          .join(' ')
      }
      return null
    },

    selectPeriodClass() {
      return this.periodSelection ? 'tw-text-neutral-600' : 'tw-text-sf-primary'
    },
    selectPeriodLabel() {
      return this.periodSelection
        ? I18n.t('companies_overview.select_another_period')
        : I18n.t('companies_overview.select_period')
    },
  },

  watch: {
    periodSelectionOpen(value) {
      if (value) {
        this.openPeriodSelection()
        this.resetPeriodSelectionOpen()
      }
    },
  },

  methods: {
    isUsingNewLayout,
    ...mapPiniaActions(useDrawer, ['closeLeftDrawer']),
    ...mapPiniaActions(useCompaniesStore, [
      'fetchFilteredCompanies',
      'receivedQueryResults',
    ]),
    ...mapPiniaActions(useSubmissionsStore, ['fetchSubmissionDropdownData']),
    ...mapPiniaActions(useUsersStore, ['fetchUsersForFirm']),
    ...mapPiniaActions(useReconciliationTextsStore, [
      'fetchReconciliationTexts',
    ]),
    ...mapPiniaActions(useTriggersStore, [
      'blankTrigger',
      'setEditedTrigger',
      'setTriggerRunning',
    ]),
    ...mapPiniaActions(useFiltersStore, [
      'editFilter',
      'fetchFilterDropdownValues',
    ]),
    ...mapPiniaActions(useSegmentsStore, ['showSegmentModal', 'fetchSegments']),
    ...mapPiniaActions(useInsightsRootStore, [
      'resetPeriodSelectionOpen',
      'setLedgerSelectDate',
      'setLedgerSelectLockType',
      'setPeriodSelection',
    ]),
    ...mapPiniaActions(useNavigationStore, [
      'loadStateFromUrlQueryParams',
      'handleFilterOrPeriodChange',
    ]),

    openPeriodSelection() {
      window.modalCitizen.openModal('insights-period-selection', {
        ledgerSelectDate: this.ledgerSelectDate,
        ledgerSelectLockType: this.ledgerSelectLockType,
        periodSelection: this.periodSelection,
        saveCallback: this.periodSelectionSave,
      })
    },

    async periodSelectionSave(data) {
      this.setLedgerSelectDate(data.ledgerSelectDate)
      this.setLedgerSelectLockType(data.ledgerSelectLockType)
      this.setPeriodSelection(data.periodSelection)
      const route = await this.handleFilterOrPeriodChange(this.$route)
      this.$router.push(route)
    },

    addTrigger() {
      if (this.addTriggerDisabled) {
        return
      }

      this.setEditedTrigger(this.blankTrigger(this.segment.id))
    },

    openFiltersModal() {
      window.modalCitizen.openModalPortal('insights-add-filter-modal')
    },
  },
}
</script>

<style scoped lang="scss">
.rs-filter-list {
  border-radius: 2px;
}
</style>
